import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, Lightbulb, ChevronLeft, Plus, Trash2, Edit, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = [
  { value: "træningstip", label: "Træningstips" },
  { value: "kost", label: "Kost" },
  { value: "restitution", label: "Restitution" },
  { value: "motivation", label: "Motivation" },
];

export default function AdminInspiration() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", category: "træningstip", image_url: "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin"); return null; }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchItems = () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    setLoading(true);
    axios.get(`${API}/admin/inspiration`, { headers })
      .then(res => setItems(res.data))
      .catch(err => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("admin_token");
          navigate("/admin");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchItems(); }, []);

  const resetForm = () => {
    setForm({ title: "", content: "", category: "træningstip", image_url: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const openEdit = (item) => {
    setForm({
      title: item.title,
      content: item.content,
      category: item.category,
      image_url: item.image_url || ""
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Udfyld titel og indhold");
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    setSaving(true);
    const payload = {
      title: form.title,
      content: form.content,
      category: form.category,
      image_url: form.image_url || null
    };

    try {
      if (editingId) {
        await axios.put(`${API}/admin/inspiration/${editingId}`, payload, { headers });
        toast.success("Inspiration opdateret");
      } else {
        await axios.post(`${API}/admin/inspiration`, payload, { headers });
        toast.success("Inspiration oprettet");
      }
      resetForm();
      fetchItems();
    } catch {
      toast.error("Fejl ved gemning");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Er du sikker på du vil slette denne inspiration?")) return;
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${API}/admin/inspiration/${id}`, { headers });
      toast.success("Inspiration slettet");
      fetchItems();
    } catch {
      toast.error("Fejl ved sletning");
    }
  };

  const getCategoryLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || cat;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#09090b] via-[#0c0c0f] to-[#09090b]">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-red-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/admin/dashboard" className="flex items-center gap-2 text-zinc-400 hover:text-white">
                <ChevronLeft className="h-5 w-5" />
              </Link>
              <div className="w-9 h-9 rounded-lg bg-red-500 flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white">
                Inspiration
              </span>
            </div>
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-red-500 hover:bg-red-600 text-white rounded-lg">
              <Plus className="h-4 w-4 mr-2" /> Ny Artikel
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-inspiration">
        <div className="mb-6">
          <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl uppercase tracking-tight text-white mb-1">
            Inspiration Administration
          </h1>
          <p className="text-sm text-zinc-400">{items.length} artikler i alt</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="bg-[#18181b] rounded-xl h-48 animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-[#18181b] border border-white/5 rounded-2xl">
            <Lightbulb className="h-16 w-16 mx-auto mb-4 text-zinc-700" />
            <p className="text-zinc-400">Ingen inspiration endnu</p>
            <Button onClick={() => setShowForm(true)} className="mt-4 bg-red-500 hover:bg-red-600">
              <Plus className="h-4 w-4 mr-2" /> Opret første artikel
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(item => (
              <div key={item.id} className="bg-[#18181b] border border-white/5 rounded-xl overflow-hidden hover:border-white/10 transition-all group">
                {item.image_url && (
                  <div className="h-32 overflow-hidden">
                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover opacity-70" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs bg-orange-500/10 text-orange-400 px-2 py-1 rounded-full">
                      {getCategoryLabel(item.category)}
                    </span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(item)} className="h-7 w-7 text-zinc-400 hover:text-white">
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteItem(item.id)} className="h-7 w-7 text-zinc-400 hover:text-red-400">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-bold text-white mb-1 line-clamp-1">{item.title}</h3>
                  <p className="text-xs text-zinc-500 line-clamp-2">{item.content}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#18181b] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase text-white">
              {editingId ? "Rediger Inspiration" : "Ny Inspiration"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Titel</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                className="bg-zinc-900/50 border-zinc-800 rounded-lg h-10 mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Kategori</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-lg h-10 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Indhold</Label>
              <textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})}
                className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 text-sm text-white mt-1 min-h-[120px]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Billede URL (valgfri)</Label>
              <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})}
                className="bg-zinc-900/50 border-zinc-800 rounded-lg h-10 mt-1" placeholder="https://..." />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={resetForm} className="flex-1 border-zinc-700">
                Annuller
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-red-500 hover:bg-red-600">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Gemmer..." : "Gem"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
